import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router';
import CheckBoxGroup from '#app/components/checkbox-group.tsx'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion'
import { Button } from '#app/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '#app/components/ui/dialog'
import { RadioGroup, RadioGroupItem } from '#app/components/ui/radio-group'
import { useLanguage } from '#app/contexts/language-context.tsx'
import { cn, useDebounce } from '#app/utils/misc.tsx'
import {
	ADDED_TO_SITE,
	EXTRAS,
	HEATING_COOLING,
	PROPERTY_CONDITION,
} from '#app/utils/providers/search-filters.ts'
import { Icon } from './ui/icon'
import { Label } from './ui/label'
import { StatusButton } from './ui/status-button'

/**
 * TODO:
 * 1. When there is something selected default the group to open
 */

interface AdvancedFiltersModalProps {
	className?: string
    isPending: boolean
	hideSearchButton?: boolean
	ignoreSearchParams?: boolean
    handleUpdateAdvanced: (advancedFilters:AdvancedFilters) => void
	handleSearch?: (filters:AdvancedFilters) => void
	resetFilters: () => void
}

export type AdvancedFilters = {
    added: string
    heating: string[]
    condition: string[]
    extras: string[]
}

export default function AdvancedFiltersModal({
	className,
    isPending,
	hideSearchButton,
	ignoreSearchParams,
    handleUpdateAdvanced,
	handleSearch,
	resetFilters
}: AdvancedFiltersModalProps) {
    const [ searchParams ] = useSearchParams();
    const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false)
    const [filters, setFilters] = useState<AdvancedFilters>({
		added: searchParams.get('added') || 'any',
		heating: searchParams.getAll('heating') || [],
		condition: searchParams.getAll('condition') || [],
		extras: searchParams.getAll('extras') || []
	})
	let { t, currentLang } = useLanguage();

	useEffect(() => {
		if (!ignoreSearchParams) {
			setFilters({
				added: searchParams.get('added') || 'any',
				heating: searchParams.getAll('heating') || [],
				condition: searchParams.getAll('condition') || [],
				extras: searchParams.getAll('extras') || []
			})
		}
	}, [ignoreSearchParams, searchParams])

	const handleSearchDebounced = useDebounce((filters:AdvancedFilters) => {
		if (handleSearch) {
			handleSearch(filters)
		}
	}, 200)


    const handleSearchClick = () => {
        setAdvancedFiltersOpen(false)

        handleSearchDebounced(filters)
    }

    const handleOpenChange = (open:boolean) => {
        if (!open) {
            //If we are hiding update our parent about our values
            handleUpdateAdvanced(filters)
        }

        setAdvancedFiltersOpen(open)
    }

    const handleFilterChange = (field:string, value:string|string[]) => {
		setFilters((prev) => ({
			...prev,
			[field]: value,
		}))
	}


	return (
		<Dialog
			open={advancedFiltersOpen}
			onOpenChange={handleOpenChange}
		>
			<DialogTrigger asChild>
				<Button
					variant="secondary"
					className={cn("flex h-12 items-center justify-center", className)}
				>
					<Icon name="gear" size="md" className="mr-1" />
					<span>{t('Search', 'Filters')}</span>
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px] md:max-w-[525px]">
				<DialogHeader>
					<DialogTitle className='flex flex-row items-center gap-2 h-full' style={{marginBlockStart: '0'}}>
						<Icon name="gear" size="md" className="mr-1" />
						{t('Search', 'Advanced filters')}
					</DialogTitle>
				</DialogHeader>
				<Accordion type="single" defaultValue={'added'}>
					<AccordionItem value="added" className="border-none">
						<AccordionTrigger className="mb-4 rounded-lg bg-secondary p-2 text-secondary-foreground hover:bg-secondary/80 hover:text-accent-foreground">
							{t('Search', 'Added to site')}
						</AccordionTrigger>
						<AccordionContent>
							<RadioGroup
								name="added"
								defaultValue={filters.added}
								className="grid grid-cols-2 gap-4"
								onValueChange={(val) => handleFilterChange('added', val)}
							>
								{ADDED_TO_SITE.map((item, idx) => (
									<RadioGroupItem
										key={`${item.id}-${idx}`}
										value={item.id}
										id={item.id}
									>
										<Label htmlFor={item.id}>{currentLang === 'en' ? item.label : item.labelGR}</Label>
									</RadioGroupItem>
								))}
							</RadioGroup>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value="heating" className="border-none">
						<AccordionTrigger className="mb-4 rounded-lg bg-secondary p-2 text-secondary-foreground hover:bg-secondary/80 hover:text-accent-foreground">
							{t('Search', 'Heating / Cooling')}
						</AccordionTrigger>
						<AccordionContent>
							<CheckBoxGroup
								name="heating"
								value={filters.heating}
								className="grid grid-cols-2 gap-4"
								items={HEATING_COOLING}
								onChange={handleFilterChange}
							/>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value="condition" className="border-none">
						<AccordionTrigger className="mb-4 rounded-lg bg-secondary p-2 text-secondary-foreground hover:bg-secondary/80 hover:text-accent-foreground">
							{t('Search', 'Property condition')}
						</AccordionTrigger>
						<AccordionContent>
							<CheckBoxGroup
								name="condition"
								value={filters.condition}
								className="grid grid-cols-2 gap-4"
								items={PROPERTY_CONDITION}
								onChange={handleFilterChange}
							/>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value="extras" className="border-none">
						<AccordionTrigger className="mb-4 rounded-lg bg-secondary p-2 text-secondary-foreground hover:bg-secondary/80 hover:text-accent-foreground">
							{t('Search', 'Extras')}
						</AccordionTrigger>
						<AccordionContent>
							<CheckBoxGroup
								name="extras"
								value={filters.extras}
								className="grid grid-cols-2 md:grid-cols-3 gap-4"
								items={EXTRAS}
								onChange={handleFilterChange}
							/>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
				{!hideSearchButton && (
					<DialogFooter>
						<StatusButton
							type="button"
							disabled={isPending}
							status={isPending ? 'pending' : 'idle'}
							className="flex h-12 items-center justify-center"
							onClick={handleSearchClick}
						>
							<Icon name="magnifying-glass" size="lg" className="mr-1" />
							<span className="not-sr-only">{t('Common', 'Search')}</span>
						</StatusButton>
					</DialogFooter>
				)}

				<Button
						className='absolute right-12 top-4 h-8 w-8'
						variant="ghost"
						size={'icon'}
						onClick={() => {
							setFilters({
								added: 'any',
								heating: [],
								condition: [],
								extras: []
							})
							setAdvancedFiltersOpen(false)
							resetFilters()
						}}
					>
						<Icon name="update" size="md" />
						<span className='sr-only'>{t('Validation', 'Clear')}</span>
					</Button>
			</DialogContent>
		</Dialog>
	)
}
